<template>
  <div class="sidebar" v-show="currentSection !== 1">
    <div
      v-for="item in sidebarItems"
      :key="item.id"
      :class="['sidebar-item', `sidebar-${item.id}`, { active: currentSection === item.id }]"
      @click="scrollToSection(item.id)"
    ></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

// 导航项配置
const sidebarItems = [
  { id: 1, label: '首页', selector: '.header-section' },
  { id: 2, label: '里程碑', selector: '.milestone-section' },
  { id: 3, label: '积分抽奖', selector: '.lottery-section' },
  { id: 4, label: '版本内容', selector: '.version-content-section' },
];

// 当前激活的板块
const currentSection = ref(1);

// IntersectionObserver 实例
let observer = null;

// 滚动到指定板块
const scrollToSection = (sectionId) => {
  const item = sidebarItems.find(item => item.id === sectionId);
  if (!item) return;

  const targetElement = document.querySelector(item.selector);
  if (targetElement) {
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};

// 初始化 Intersection Observer
const initObserver = () => {
  // 存储每个 section 的可见比例
  const visibilityMap = new Map();

  // 创建 IntersectionObserver
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const sectionId = parseInt(entry.target.dataset.sectionId);
        if (!isNaN(sectionId)) {
          // 更新该 section 的可见比例
          visibilityMap.set(sectionId, entry.intersectionRatio);
        }
      });

      // 找出可见比例最大的 section
      let maxVisibility = 0;
      let mostVisibleSection = 1;

      visibilityMap.forEach((ratio, id) => {
        if (ratio > maxVisibility) {
          maxVisibility = ratio;
          mostVisibleSection = id;
        }
      });

      // 更新当前激活的 section
      if (maxVisibility > 0) {
        currentSection.value = mostVisibleSection;
      }
    },
    {
      // 配置选项
      root: null, // 使用视口作为根元素
      rootMargin: '-20% 0px -20% 0px', // 中间 60% 区域触发
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0], // 多个阈值，更精确
    }
  );

  // 观察所有 section 元素
  sidebarItems.forEach((item) => {
    const element = document.querySelector(item.selector);
    if (element) {
      element.dataset.sectionId = item.id;
      observer.observe(element);
    }
  });
};

onMounted(() => {
  // 延迟初始化，确保 DOM 已渲染
  setTimeout(() => {
    initObserver();
  }, 100);
});

onUnmounted(() => {
  // 断开所有观察
  if (observer) {
    observer.disconnect();
    observer = null;
  }
});
</script>

<style lang="less" scoped>
// 基准宽度：1920px
// 转换公式：vw = (px / 1920) * 100

.sidebar {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  right: 1.979vw; // 38px / 1920 * 100
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.521vw; // 10px / 1920 * 100
  width: 6.875vw; // 132px / 1920 * 100
  height: 16.198vw; // 311px / 1920 * 100
  background-image: url(../assets/images/sidebar/sidebar.png);
  background-size: 100% 100%;
  padding: 1.042vw 0; // 20px / 1920 * 100
  justify-content: flex-end;
  padding-bottom: 0;
}

.sidebar-item {
  width: 7.344vw; // 141px / 1920 * 100
  height: 2.083vw; // 40px / 1920 * 100
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  // 各个导航按钮的默认图标
  &.sidebar-1 {
    background-image: url(../assets/images/sidebar/sidebar-1.png);
  }

  &.sidebar-2 {
    background-image: url(../assets/images/sidebar/sidebar-2.png);
  }

  &.sidebar-3 {
    background-image: url(../assets/images/sidebar/sidebar-3.png);
  }

  &.sidebar-4 {
    background-image: url(../assets/images/sidebar/sidebar-4.png);
  }

  // 激活状态的图标
  &.sidebar-1.active {
    background-image: url(../assets/images/sidebar/sidebar-1-active.png);
  }

  &.sidebar-2.active {
    background-image: url(../assets/images/sidebar/sidebar-2-active.png);
  }

  &.sidebar-3.active {
    background-image: url(../assets/images/sidebar/sidebar-3-active.png);
  }

  &.sidebar-4.active {
    background-image: url(../assets/images/sidebar/sidebar-4-active.png);
  }
}
</style>
